// src/components/Step2Ocean.js
import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { Row, Col, Button, Tooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as LclIcon } from 'icons/lcl.svg';
import { ReactComponent as Dc20Icon } from 'icons/20dc.svg';
import { ReactComponent as Dc40Icon } from 'icons/40dc.svg';
import { ReactComponent as Hc40Icon } from 'icons/40hc.svg';
import { setActiveFlow, setCurrentStep, setFormData } from 'store/slices/flowSlice';
import { useDispatch } from 'react-redux';
import { useTranslation } from "react-i18next";

const Step2Ocean = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState({
    lcl: false,
    dc20: false,
    dc40: false,
    hc40: false,
  });

  const serviceOptions = [
    {
      value: 'lcl',
      title: t('LCL'),
      icon: LclIcon,
      tooltip: `LCL stands for less than a container load.
  Since LCL shipments fill less than a full
  shipping container, they are grouped with
  other cargo.`,
    },
    {
      value: 'dc20',
      title: t('20′ DC'),
      icon: Dc20Icon,
      tooltip: `20' DC (Standard/Dry Container)
  Exterior Dimensions (in feet): 20' long x 8' wide x 8'6" high
  Exterior Dimensions (in meters): 6.10m long x 2.44m wide x 2.59m high
  Interior Dimensions (in feet): 19'4" long x 7'9" wide x 7'10" high
  Interior Dimensions (in meters): 5.898m long x 2.352m wide x 2.393m high
  Usable Capacity: 32.6m3`,
    },
    {
      value: 'dc40',
      title: t('40′ DC'),
      icon: Dc40Icon,
      tooltip: `40' DC (Standard/Dry Container)
  Exterior Dimensions (in feet): 40' long x 8' wide x 8'6" high
  Exterior Dimensions (in meters): 12.19m long x 2.44m wide x 2.59m high
  Interior Dimensions (in feet): 39'6" long x 7'9" wide x 7'10" high
  Interior Dimensions (in meters): 12.025m long x 2.352m wide x 2.393m high
  Usable Capacity: 67.7m3`,
    },
    {
      value: 'hc40',
      title: t('40′ HC'),
      icon: Hc40Icon,
      tooltip: `40' HC (High Cube Container)
  Exterior Dimensions (in feet): 40' long x 8' wide x 8'6" high
  Exterior Dimensions (in meters): 12.19m long x 2.44m wide x 2.99m high
  Interior Dimensions (in feet): 39'6" long x 7'9" wide x 8'10" high
  Interior Dimensions (in meters): 12.025m long x 2.352m wide x 2.585m high
  Usable Capacity: 76m3`,
    },
    {
      value: 'ot',
      title: t('OT'),
      icon: Hc40Icon,
      tooltip: `OT (Open Top Container)
  Exterior Dimensions (in feet): 40' long x 8' wide x 8'6" high
  Exterior Dimensions (in meters): 12.19m long x 2.44m wide x 2.99m high
  Interior Dimensions (in feet): 39'6" long x 7'9" wide x 8'10" high
  Interior Dimensions (in meters): 12.025m long x 2.352m wide x 2.585m high
  Usable Capacity: 76m3`,
    },
    {
      value: 'rf',
      title: t('RF'),
      icon: Hc40Icon,
      tooltip: `RF (Refrigerated Container (Reefer))
  Exterior Dimensions (in feet): 40' long x 8' wide x 8'6" high
  Exterior Dimensions (in meters): 12.19m long x 2.44m wide x 2.99m high
  Interior Dimensions (in feet): 39'6" long x 7'9" wide x 8'10" high
  Interior Dimensions (in meters): 12.025m long x 2.352m wide x 2.585m high
  Usable Capacity: 76m3`,
    },
    {
      value: 'fr',
      title: t('FR'),
      icon: Hc40Icon,
      tooltip: `FR (Flat Rack Container)
  Exterior Dimensions (in feet): 40' long x 8' wide x 8'6" high
  Exterior Dimensions (in meters): 12.19m long x 2.44m wide x 2.99m high
  Interior Dimensions (in feet): 39'6" long x 7'9" wide x 8'10" high
  Interior Dimensions (in meters): 12.025m long x 2.352m wide x 2.585m high
  Usable Capacity: 76m3`,
    },
    {
      value: 'other',
      title: t('Other Specialized'),
      icon: Hc40Icon,
      tooltip: `Other Specialized
  Exterior Dimensions (in feet): 40' long x 8' wide x 8'6" high
  Exterior Dimensions (in meters): 12.19m long x 2.44m wide x 2.99m high
  Interior Dimensions (in feet): 39'6" long x 7'9" wide x 8'10" high
  Interior Dimensions (in meters): 12.025m long x 2.352m wide x 2.585m high
  Usable Capacity: 76m3`,
    },
  ];

  const toggleTooltip = (value) => {
    setTooltipOpen((prev) => ({ ...prev, [value]: !prev[value] }));
  };

  const handleSubmit = (values) => {
    dispatch(setActiveFlow('ocean'));
    dispatch(setCurrentStep('2'));
    dispatch(setFormData(values));
  };

  return (
    <div className='container'>
      <Formik
        initialValues={{
          selectedService: '',
        }}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <h4 style={{ fontSize: '32px' }}>{t("Great. Please select a service")}</h4>
            <Row>
              {serviceOptions.map((option, index) => {
                const IconComponent = option.icon;
                const id = `service-${option.value}`; // Add a valid prefix to the id
                return (
                  <Col key={index} sm="3" className="mb-3 bg-light e2g-ocean-service">
                    <div className="e2g-service-option service-option h-100">
                      <Field
                        type="radio"
                        name="selectedService"
                        value={option.value}
                        id={id}
                        className="e2g-service-option-radio d-none h-100"
                        onChange={() => setFieldValue('selectedService', option.value)}
                      />
                      <label htmlFor={id} className="e2g-service-option-label service-option-label h-100">
                        <IconComponent className="e2g-service-option-icon service-option-icon" />
                        <h5>{t(option.title)} <FontAwesomeIcon icon={faInfoCircle} id={`info-${option.value}`} /></h5>
                        <Button
                          type="submit"
                          color="primary"
                          onClick={() => { setFieldValue('selectedService', option.title) }}
                        >
                          {t("Select")}
                        </Button>
                      </label>
                      <Tooltip
                        placement="right"
                        isOpen={tooltipOpen[option.value]}
                        target={`info-${option.value}`}
                        toggle={() => toggleTooltip(option.value)}
                      >
                        {t(option.tooltip)}
                      </Tooltip>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Step2Ocean;
